import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AlertTriangle, Home, RefreshCw, ArrowLeft, ServerCrash,
  WifiOff, ShieldAlert, FileQuestion, AlertCircle,
} from 'lucide-react';

const ErrorPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // ✅ Error details from navigation state or URL
  const errorCode = location.state?.code || new URLSearchParams(location.search).get('code') || '500';
  const errorMessage =
    location.state?.message ||
    new URLSearchParams(location.search).get('message') ||
    'Something went wrong. Please try again.';
  const errorDetails = location.state?.details || null;

  // ✅ Error config based on code
  const getErrorConfig = (code) => {
    const configs = {
      '400': {
        icon: AlertCircle,
        title: 'Bad Request',
        description: 'The request was invalid. Please check your input and try again.',
        color: 'var(--color-warning)',
      },
      '401': {
        icon: ShieldAlert,
        title: 'Unauthorized',
        description: 'You need to login to access this page.',
        color: 'var(--color-warning)',
      },
      '403': {
        icon: ShieldAlert,
        title: 'Access Denied',
        description: "You don't have permission to access this resource.",
        color: 'var(--color-danger)',
      },
      '404': {
        icon: FileQuestion,
        title: 'Page Not Found',
        description: "The page you're looking for doesn't exist or has been moved.",
        color: 'var(--color-info)',
      },
      '500': {
        icon: ServerCrash,
        title: 'Internal Server Error',
        description: 'Something went wrong on our server. Please try again later.',
        color: 'var(--color-danger)',
      },
      '502': {
        icon: ServerCrash,
        title: 'Bad Gateway',
        description: 'The server received an invalid response. Please try again later.',
        color: 'var(--color-danger)',
      },
      '503': {
        icon: ServerCrash,
        title: 'Service Unavailable',
        description: 'The server is temporarily unavailable. Please try again later.',
        color: 'var(--color-danger)',
      },
      'network': {
        icon: WifiOff,
        title: 'Network Error',
        description: 'Cannot connect to server. Please check your internet connection.',
        color: 'var(--color-warning)',
      },
      'default': {
        icon: AlertTriangle,
        title: 'Something Went Wrong',
        description: errorMessage,
        color: 'var(--color-danger)',
      },
    };

    return configs[String(code)] || configs.default;
  };

  const config = getErrorConfig(errorCode);
  const Icon = config.icon;

  const handleGoHome = () => navigate('/');
  const handleGoBack = () => navigate(-1);
  const handleReload = () => window.location.reload();

  return (
    <div className="error-page">
      {/* Background */}
      <div className="error-background">
        <div className="error-overlay" />
      </div>

      {/* Content */}
      <div className="error-container">
        <div className="error-box">
          {/* Icon */}
          <div className="error-icon-wrapper" style={{ background: `${config.color}15`, color: config.color }}>
            <Icon size={64} strokeWidth={1.5} />
          </div>

          {/* Code */}
          <h1 className="error-code" style={{ color: config.color }}>
            {errorCode}
          </h1>

          {/* Title */}
          <h2 className="error-title">{config.title}</h2>

          {/* Description */}
          <p className="error-description">
            {errorMessage || config.description}
          </p>

          {/* Details (dev mode) */}
          {errorDetails && process.env.NODE_ENV === 'development' && (
            <details className="error-details">
              <summary>Error Details (Dev)</summary>
              <pre>{errorDetails}</pre>
            </details>
          )}

          {/* Actions */}
          <div className="error-actions">
            <button className="btn btn-outline" onClick={handleGoBack}>
              <ArrowLeft size={18} />
              Go Back
            </button>
            <button className="btn btn-outline" onClick={handleReload}>
              <RefreshCw size={18} />
              Reload
            </button>
            <button className="btn btn-primary" onClick={handleGoHome}>
              <Home size={18} />
              Go Home
            </button>
          </div>

          {/* Footer */}
          <div className="error-footer">
            <p>If the problem persists, please contact support.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ErrorPage;